import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useFieldArray, Control } from 'react-hook-form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DayName } from '@aphp-ema/common/scheduling';
import { NotificationFieldRow } from '@/features/scheduling/components/NotificationFieldRow';
import { SchedulingFormData } from '@/features/scheduling/types/SchedulingFormType.interface';

type SpecificDaysFormProps = {
  control: Control<SchedulingFormData>;
};

export const SpecificDaysSchedulingForm = ({
  control,
}: SpecificDaysFormProps) => {
  const { t } = useTranslation();

  const dayNameMapping: Record<DayName, string> = {
    MONDAY: t('days.monday'),
    TUESDAY: t('days.tuesday'),
    WEDNESDAY: t('days.wednesday'),
    THURSDAY: t('days.thursday'),
    FRIDAY: t('days.friday'),
    SATURDAY: t('days.saturday'),
    SUNDAY: t('days.sunday'),
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'schedulings',
  });

  const getNotificationCountForDay = (day: DayName): number => {
    return fields.filter((n) => n?.day === day).length || 0;
  };

  const [dayNotifications, setDayNotifications] = useState<
    Record<DayName, number>
  >(() => {
    const days: DayName[] = [
      'MONDAY',
      'TUESDAY',
      'WEDNESDAY',
      'THURSDAY',
      'FRIDAY',
      'SATURDAY',
      'SUNDAY',
    ];

    return days.reduce(
      (acc, day) => ({
        ...acc,
        [day]: getNotificationCountForDay(day),
      }),
      {} as Record<DayName, number>,
    );
  });

  const updateNotificationFields = (day: DayName, count: number) => {
    const existingFields = fields.filter((field) => field.day === day);
    const currentCount = existingFields.length;

    if (count > currentCount) {
      for (let i = currentCount; i < count; i++) {
        append({
          day,
          startTime: null,
          endTime: null,
        });
      }
    } else if (count < currentCount) {
      const fieldsToRemove = existingFields.slice(count);
      fieldsToRemove.forEach((field) => {
        const index = fields.findIndex((f) => f.id === field.id);
        if (index !== -1) {
          remove(index);
        }
      });
    }

    setDayNotifications((prev) => ({ ...prev, [day]: count }));
  };

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 1,
        mb: 2,
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 2,
          mb: 3,
        }}
      >
        {Object.keys(dayNotifications).map((day) => (
          <Box key={day} sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
              {dayNameMapping[day as DayName]}
            </Typography>
            <TextField
              type="number"
              size="small"
              fullWidth
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 50,
                },
              }}
              value={dayNotifications[day as DayName]}
              onChange={(e) => {
                const newValue = Math.min(50, parseInt(e.target.value) || 0);

                const value = Math.max(0, newValue);
                updateNotificationFields(day as DayName, value);
              }}
            />
          </Box>
        ))}
      </Box>
      {fields.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('pages.studies.emaQuestionnaire.scheduling.notifications')}
          </Typography>
          {Object.entries(dayNotifications)
            .filter(([, count]) => count > 0)
            .map(([day]) => {
              const dayFields = fields
                .map((field, index) => ({ field, index }))
                .filter(({ field }) => field.day === day);

              return (
                <Box key={day} sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 2, fontWeight: 'bold' }}
                  >
                    {dayNameMapping[day as DayName]}
                  </Typography>
                  {dayFields.map(({ field, index }, indexOfDay) => (
                    <NotificationFieldRow
                      key={field.id}
                      control={control}
                      index={index}
                      indexOfDay={indexOfDay}
                      dayDisplayName={dayNameMapping[day as DayName]}
                      initialShowEndTime={
                        field.startTime === null ||
                        field.startTime.diff(field.endTime) !== 0
                      }
                      onRemove={() => {
                        remove(index);
                        setDayNotifications((prev) => ({
                          ...prev,
                          [day]: prev[day as DayName] - 1,
                        }));
                      }}
                    />
                  ))}
                </Box>
              );
            })}
        </Box>
      )}
    </Box>
  );
};
